import { CenterText } from 'modules/productAnalytics/Styled';
import styled from 'styled-components';
import { InfoBanner } from 'ui/banner';
import { GhostButton } from 'ui/button';
import { Link } from 'ui/links';
import PageHeader from 'ui/pageHeader/PageHeader';
import { Skeleton } from 'ui/skeleton';
import { TableHeaderCell } from 'ui/tableHeaderCell';
import { TableRows } from 'ui/tableRows';
import { Tab } from 'ui/tabs';
import {
  body1,
  body1Bold,
  body2,
  caption,
  colors,
  fontWeights,
  heading4,
  smallShadow,
  spacing,
  subtitle1,
} from 'ui/theme';
import { pxToRem } from 'utils/convert';

export const MainWrapper = styled.div`
  display: grid;
  grid-template-columns: max-content auto;
`;
export const MainContent = styled.div`
  overflow-x: hidden;
  min-height: ${pxToRem(960)};
  padding-bottom: ${spacing.double};
`;

export const TabContent = styled.div<{ hideTabContentBackground: boolean }>`
  margin: 0 ${spacing.double};
  ${({ hideTabContentBackground }) =>
    !hideTabContentBackground
      ? `padding: ${spacing.plusHalf};
      background: ${colors.backgroundPrimary};
  border-radius: ${spacing.half};
  box-shadow: ${smallShadow.boxShadow};`
      : ''};
`;
export const SelectWrapper = styled.div`
  width: ${spacing.x24};
  margin-bottom: ${spacing.base};
`;

export const StyledApplyContainer = styled.div`
  position: sticky;
  margin: 0 -${spacing.double};
  bottom: 0;
  left: 0;
  display: flex;
  z-index: 9999999;
  background: ${colors.backgroundPrimary};
  min-width: ${pxToRem(392)};
  max-width: ${pxToRem(392)};
  padding: ${spacing.base} ${spacing.double};
  justify-content: space-between;
  border-top: ${spacing.single} solid ${colors.borderPrimary};
  border-right: ${spacing.single} solid ${colors.borderPrimary};
`;

export const SectionWrapper = styled.div`
  padding: 0 ${spacing.double};
`;

export const StyledPageHeader = styled(PageHeader)`
  padding: ${spacing.double};
  margin: 0;
  background-color: ${colors.backgroundPrimary};
  border-bottom: ${spacing.single} solid ${colors.borderPrimary};
  align-items: center;
  justify-content: space-between;
`;

export const PageHeaderContentWrapper = styled.div`
  display: flex;
  align-items: center;
  > * {
    padding-right: ${spacing.base};
  }
`;

export const StyledTabGroupWrapper = styled.div`
  padding: 0 ${spacing.double} 0 ${spacing.plusQuarter};
  margin-bottom: ${spacing.double};
  border-bottom: ${spacing.single} solid ${colors.borderPrimary};
  background: ${colors.backgroundPrimary};

  div {
    border-bottom: none;
  }
`;

export const StyledTab = styled(Tab)`
  ${body1}
  font-weight: normal;
  height: ${spacing.x4};
  margin-right: ${spacing.half};
  color: ${({ isSelected }) => (isSelected ? colors.purple100 : colors.textSecondary)};
  border-width: ${spacing.quarter};
  border-bottom-color: ${({ isSelected }) => (isSelected ? colors.purple100 : 'transparent')};
`;

export const StyledTabPanel = styled.div``;

export const SelectDropdownInfoText = styled.div`
  padding: ${spacing.base};
  color: ${colors.textSecondary};
`;

export const StyledInfoBanner = styled(InfoBanner)`
  display: flex;
  align-items: center;
  margin: ${spacing.double} ${spacing.double} ${spacing.plusHalf};
`;

export const StyledAnchor = styled(Link)`
  color: inherit;
  text-decoration-color: inherit;
`;

export const Container = styled.div`
  background: ${colors.backgroundPrimary};
  border-radius: ${spacing.half};
  ${smallShadow};
`;

export const CampaignCaption = styled.div`
  ${caption};
  display: flex;
  align-items: center;
  color: ${colors.textSecondary};
`;

export const CampaignCaptionItem = styled.span`
  &:not(:first-child)::before {
    color: ${colors.grey35};
    content: '\\2219'; /** unicode for bullet operators */
    margin: 0 ${spacing.quarter};
    font-weight: ${fontWeights.semiBold};
  }
`;

export const CampaignsHeader = styled.div`
  align-items: center;
  ${body1Bold};
  padding: ${spacing.base} ${spacing.plusHalf};
  border-bottom: ${spacing.single} solid ${colors.borderPrimary};
  display: flex;
  justify-content: space-between;
`;

export const TableWrapper = styled.div`
  height: auto;
  overflow: auto;
`;

export const StyledTableHeaderCell = styled(TableHeaderCell)<{ align: string }>`
  height: ${spacing.x3};
  padding-right: ${(props) => {
    return props.align === 'flex-end' ? '' : spacing.double;
  }};
  vertical-align: middle;
  border-bottom: ${colors.borderPrimary} ${spacing.single} solid;
  padding-left: ${spacing.double};
  &:first-child {
    position: sticky;
    left: 0;
    box-shadow: inset -${spacing.quarter} ${spacing.single} ${spacing.half} -${pxToRem(6)}
      rgb(0 0 0 / 0.12);
    width: auto;
    padding-left: ${spacing.base};
    text-align: center;
    z-index: 2;
    & > div {
      display: flex;
      justify-content: left;
      & > svg {
        margin-left: ${spacing.quarter};
      }
    }
  }
`;

export const StyledTableRows = styled(TableRows)`
  > div {
    padding-left: ${spacing.double};
    &:first-child {
      position: sticky;
      z-index: 2;
      left: 0;
      vertical-align: middle;
      background: inherit;
      padding-left: ${spacing.base};
      box-shadow: inset -${spacing.quarter} ${spacing.single} ${spacing.half} -${pxToRem(6)}
        rgb(0 0 0 / 0.12);
    }
    &:not(:first-child) {
      white-space: nowrap;
    }
  }
`;

export const ControlRow = styled.div`
  display: flex;
  padding: ${spacing.double} 0;
`;

export const SideCol = styled.div`
  flex: 0 1 25%;
  display: flex;
  justify-content: flex-end;
  padding-left: ${spacing.double};
  padding-right: ${spacing.double};
`;

export const CenterCol = styled.div`
  flex: 0 1 50%;
  display: flex;
  justify-content: center;
`;

export const PreviewContainer = styled.img`
  width: ${spacing.x4};
  height: ${spacing.x3};
  border-radius: ${spacing.threeEights};
  border: ${spacing.single} solid ${colors.borderPrimarySubtle};
  object-fit: cover;
  object-position: top;
`;

export const StyledSkeleton = styled(Skeleton)`
  margin-right: ${spacing.half};
  width: ${pxToRem(100)};
`;

export const StyledImgSkeleton = styled(Skeleton)`
  margin-right: ${spacing.half};
  width: ${pxToRem(64)};
  height: ${pxToRem(48)};
`;

export const ArrowButton = styled(GhostButton)`
  cursor: pointer;
  flex: 0 0 auto;
  margin-right: ${spacing.quarter};
  margin-left: ${spacing.base};
`;

export const FlexAlignCenter = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.quarter};

  > span {
    display: flex;
    align-items: center;
  }
`;

export const DeleteViewsListContainer = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  padding: 0;
`;

export const DeleteViewsList = styled.ul`
  margin: 0;
  border: ${spacing.single} solid ${colors.borderPrimary};
  background: ${colors.backgroundSecondary};
  padding: ${spacing.threeQuarter} ${spacing.base};
  list-style-type: none;
  border-radius: ${spacing.quarter};
  max-height: ${spacing.x24};
  overflow: auto;

  li {
    ${body2}
    padding: ${spacing.eighth} 0;
  }
`;

export const BoldSpan = styled.span`
  font-weight: ${fontWeights.bold};
`;

export const ErrorStateContainer = styled.div`
  text-align: center;
  padding: ${spacing.x3};
  margin: ${spacing.x4} 0;
`;

export const ErrorStateTitle = styled.div`
  ${subtitle1}
  ${heading4}
  margin-top: ${spacing.quarter};
`;

export const ErrorStateSubtitle = styled.div`
  ${body1}
  margin-top: ${spacing.quarter};
  > a {
    color: ${colors.purple100};
  }
`;

export const PaddingRight = styled.div`
  padding-right: ${spacing.base};
`;

export const ChildrenCell = styled.div`
  ${body2};
  box-sizing: border-box;
  display: table-cell;
  overflow-y: visible;
  padding: ${spacing.threeQuarter};
  margin: 0;
  height: ${pxToRem(45)};
  vertical-align: middle;
  color: ${colors.secondaryText};
`;

export const CenterTextSpaceBetween = styled(CenterText)`
  justify-content: space-between;
`;

export const SegCell = styled.div<{ metric?: boolean }>`
  ${body2};
  box-sizing: border-box;
  display: table-cell;
  height: ${spacing.double};
  margin: 0;
  overflow-y: visible;
  padding-left: ${({ metric }) => (metric ? spacing.threeQuarter : 0)};
  padding-right: ${({ metric }) => (metric ? spacing.threeQuarter : 0)};
  vertical-align: middle;
`;
